import { useToasts } from "react-toast-notifications";

import { Button } from "@components/v2/buttons/button";
import { useWizardContext } from "@components/v2/wizard/hooks";
import { CREATE_DATA_EXPORT_V2 } from "@features/data-export-v2/data-export-v2-queries";
import { useScheduleExportFormContext } from "@features/data-export-v2/hooks/useScheduleExportFormContext";
import { FETCH_SCHEDULED_EXPORTS } from "@gql/data-export";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type CreateDataExportV2,
  type CreateDataExportV2Variables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";

type ScheduleExportFooterProps = {
  onCancel?(): void;
  onFinish?(): void;
};

export const ScheduleExportFooter = ({
  onCancel,
  onFinish,
}: ScheduleExportFooterProps) => {
  const { addToast } = useToasts();
  const { navigateToNextStep, nextStep } = useWizardContext();
  const { handleSubmit, formState } = useScheduleExportFormContext();

  const [createDataExportV2, { loading: isCreatingExportV2 }] =
    useNorthbeamMutation<CreateDataExportV2, CreateDataExportV2Variables>(
      CREATE_DATA_EXPORT_V2,
      {
        onError: () => {
          addToast("Error on creating export", {
            appearance: "error",
          });
        },
        refetchQueries: [
          { query: FETCH_SCHEDULED_EXPORTS, variables: { page: 1 } },
        ],
        awaitRefetchQueries: true,
        doNotRaiseErrorOnFailure: true,
      },
    );

  const isFormDirty = formState.isDirty;
  const isValid = formState.isValid;

  const onSubmit = handleSubmit(async (data) => {
    const { isBucketValid: _, ...destinationFields } = data.destination;
    const exportV2Payload = {
      ...data,
      destination: destinationFields,
    };
    await createDataExportV2({
      variables: {
        data: exportV2Payload,
      },
    });
    onFinish?.();
  });
  return (
    <div className="flex gap-2 justify-end items-center px-6 py-3 border-t h-inherit border-v2-tertiary">
      <Button variant="tertiary" onClick={onCancel}>
        CANCEL
      </Button>
      {nextStep && (
        <Button variant="primary" onClick={navigateToNextStep}>
          Next
        </Button>
      )}
      {!nextStep && (
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={!isFormDirty || !isValid}
          loading={isCreatingExportV2}
        >
          Create export
        </Button>
      )}
    </div>
  );
};
